import { SupportedLanguage } from "../../../interfaces";

type RoomTranslationValue = {
  name: string;
  description?: string | null;
  shortDescription?: string | null;
};

/**
 * Minimal shape consumed by the room-text resolvers — matches every api room
 * shape. `en` is the canonical, schema-guaranteed translation
 * (`roomsTranslationsSchema.en` is non-nullable with a `mandatoryString` name), so
 * it is always present. There is no top-level `name`/`description` anymore.
 */
type RoomTranslationInput = {
  translations: Record<SupportedLanguage, RoomTranslationValue | null> & {
    en: RoomTranslationValue;
  };
};

/**
 * The description resolvers additionally need to know which languages the room
 * PUBLISHES — that is what decides whether a missing description is a content gap
 * worth hiding or a language the room never promised.
 *
 * `allowedLanguages` is not an approximate field pressed into service for want of a
 * better one. It is a PUBLISHING PROMISE about content — it drives the translation
 * fan-out at ingestion (`parsing-article-creation`) and room attachment for
 * translated articles and playlists (`translate-article`, `playlist-translate`), as
 * well as the in-room language picker. Which makes it the exact right thing to gate
 * on: the room declared it publishes in French, so serving a French reader English
 * copy as "the description" breaks that declaration. The blanking is meaningful
 * BECAUSE the field is a promise rather than a hint.
 *
 * Split from `RoomTranslationInput` rather than widened into it: `getRoomName` never
 * consults the field, and one call site — `PlaylistPageHeader`, reading the room
 * nested inside a playlist wire shape — genuinely does not carry
 * `allowedLanguages`. Widening the shared input would have forced an api projection
 * change to satisfy a resolver that does not use it.
 */
type RoomDescriptionInput = RoomTranslationInput & {
  allowedLanguages: ReadonlyArray<SupportedLanguage>;
};

/**
 * A room's display name in `language`, falling back to the canonical `en`.
 *
 * The name fallback STAYS. Unlike a description, a name is never optional — `en.name`
 * is contract-guaranteed non-empty — so falling back always yields something real,
 * and a nameless room in the UI is worse than a name in the wrong language.
 *
 * Which `language` to pass is the CALLER's decision and is deliberately not defaulted
 * here. `useRoomName` used to hide that choice, which is why 11 sites silently read
 * the interface locale for text that belongs to the content axis.
 */
export function getRoomName(
  room: RoomTranslationInput,
  language: SupportedLanguage
): string {
  const active = room.translations[language];
  if (active !== null && active !== undefined) return active.name;
  return room.translations.en.name;
}

/** `""`, `null` and `undefined` all mean "nothing authored". */
function authored(value: string | null | undefined): string | null {
  return value === null || value === undefined || value === "" ? null : value;
}

/**
 * A room's description in `language`.
 *
 * NO FALLBACK when `language` is one the room publishes. If a room says it publishes
 * French and has no French description, that is a content gap — showing English to a
 * reader who asked for French misrepresents it as translated.
 *
 * The fallback is RETAINED when `language` is NOT in `allowedLanguages`. That is not a
 * nicety: it is the case where the reader is seeing the room in a language the room
 * never promised, so English is an honest best effort rather than a substitution. Two
 * real paths reach it, and both would otherwise blank descriptions that render fine
 * today:
 *   • `?lang=<code>` forces both language axes with no `allowedLanguages` check
 *     (`LangSync`) — the marketing-asset screenshot pipeline depends on it;
 *   • `useActiveLang` derives `contentLang` from an fr/en guess when no in-room
 *     override is set, so a de-only room can resolve to `en`.
 */
export function getRoomDescription(
  room: RoomDescriptionInput,
  language: SupportedLanguage
): string | null {
  const active = room.translations[language];
  const activeValue = authored(
    active !== null && active !== undefined ? active.description : null
  );
  if (activeValue !== null) return activeValue;
  if (room.allowedLanguages.includes(language)) return null;
  return authored(room.translations.en.description);
}

/** Same gate as `getRoomDescription`, for the onboarding suggestion cards. */
export function getRoomShortDescription(
  room: RoomDescriptionInput,
  language: SupportedLanguage
): string | null {
  const active = room.translations[language];
  const activeValue = authored(
    active !== null && active !== undefined ? active.shortDescription : null
  );
  if (activeValue !== null) return activeValue;
  if (room.allowedLanguages.includes(language)) return null;
  return authored(room.translations.en.shortDescription);
}
